import {isTestEnv} from 'common/report/util/env';

// Frigade's assistant ships as a CDN script plus a `window.FrigadeAssistant`
// global, not an npm package. Before the script loads, the global is a stub:
// calling it queues configs on `.q`, and `.on()` queues subscriptions on
// `.on.q`. The imperative methods (`show`, `hide`, `close`, `getMode`, `ask`)
// are attached only once the script has loaded and are NOT queued, so every
// call site must check that the method exists before calling it.
// Docs: https://docs.frigade.com
export type FrigadeAssistantMode =
  | `hidden`
  | `collapsed`
  | `input`
  | `thinking`
  | `streaming-result`
  | `result`
  | `reply-input`
  | `tool-ui`
  | `error`;

interface FrigadeAssistantConfig {
  // The vendor's field is `apiKey`; `publicKey` is our name for the same value
  // (that is what `user_support_info` calls it). Sending `publicKey` makes the
  // vendor throw "Please provide a Frigade public API key" and abort its init.
  apiKey: string;
  // Required: the vendor ships no default and throws without it.
  apiUrl: string;
  assistantId: string;
  userId: string;
  userProperties?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  tools?: Record<string, unknown>;
  defaultVisible?: boolean;
}

type FrigadeEventHandler = (...args: unknown[]) => void;

type FrigadeOnFn = ((event: string, handler: FrigadeEventHandler) => void) & {
  q?: Array<{event: string; handler: FrigadeEventHandler}>;
};

type FrigadeAssistantApi = ((config: FrigadeAssistantConfig) => void) & {
  q?: FrigadeAssistantConfig[];
  on?: FrigadeOnFn;
  show?: () => void;
  hide?: () => void;
  close?: () => void;
  getMode?: () => FrigadeAssistantMode;
  ask?: (question: string) => void;
};

declare global {
  interface Window {
    FrigadeAssistant?: FrigadeAssistantApi;
  }
}

const FRIGADE_SCRIPT_SRC = `https://app.frigade.com/cdn/frigade-assistant.min.js`;
// Paired with the `api.frigade.ai` connect-src entries in
// kube/lib/webapp/nginx/csp-config.libsonnet - changing one needs the other.
const FRIGADE_API_URL = `https://api.frigade.ai`;

export interface LoadFrigadeAssistantOptions {
  publicKey: string;
  assistantId: string;
  // Required: the vendor throws "Please provide a userId to initialize Frigade"
  // without it, and it keys the conversation on their side.
  userId: string;
  userProperties?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  tools?: Record<string, unknown>;
}

// Kick off the script download without identifying or showing the assistant.
// Safe to call repeatedly: `loadFrigadeAssistant` dedupes via
// `_frigadeReadyPromises`.
export function prewarmFrigadeAssistant(publicKey: string, assistantId: string, userId: string): void {
  if (!publicKey || !assistantId || !userId) {
    return;
  }
  void loadFrigadeAssistant({publicKey, assistantId, userId});
}

export function showFrigadeAssistant(): void {
  window.FrigadeAssistant?.show?.();
}

// Frigade drops an in-flight response when hidden mid-answer rather than
// saving it - callers should consult `getFrigadeMode()` before hiding while
// the assistant is thinking/streaming.
export function hideFrigadeAssistant(): void {
  window.FrigadeAssistant?.hide?.();
}

/**
 * @lintignore (knip) TODO(GRO-252): temporarily unreferenced — the visibility lifecycle work discards the conversation when the assistant is dismissed
 */
export function closeFrigadeConversation(): void {
  window.FrigadeAssistant?.close?.();
}

/**
 * @lintignore (knip) TODO(GRO-252): temporarily unreferenced — the visibility lifecycle work consults the mode before hiding mid-answer
 */
export function getFrigadeMode(): FrigadeAssistantMode | undefined {
  return window.FrigadeAssistant?.getMode?.();
}

/**
 * `ask()` works on a hidden assistant and brings it back on screen by itself.
 *
 * @lintignore (knip) TODO(GRO-250): temporarily unreferenced — consumed by the entry points that seed the assistant with a question
 */
export function askFrigadeAssistant(question: string): void {
  window.FrigadeAssistant?.ask?.(question);
}

// Frigade's `on()` does not dedupe handlers, so register once per page.
let _listenersRegistered = false;

/**
 * Wires event subscriptions through the queue-able `on()` stub, so it is safe
 * to call before the script has loaded. No-ops until the bootstrap has
 * installed the stub, and on every call after the first successful one.
 *
 * @lintignore (knip) TODO(GRO-254): temporarily unreferenced — the conversation-tracking work subscribes to the assistant's events
 */
export function registerFrigadeEventHandlers(handlers: Record<string, FrigadeEventHandler>): void {
  if (_listenersRegistered) {
    return;
  }
  const on = window.FrigadeAssistant?.on;
  if (!on) {
    return;
  }
  for (const [event, handler] of Object.entries(handlers)) {
    on(event, handler);
  }
  _listenersRegistered = true;
}

function installFrigadeBootstrap(): HTMLScriptElement {
  if (!window.FrigadeAssistant) {
    const stub = function (config: FrigadeAssistantConfig) {
      (stub.q = stub.q ?? []).push(config);
    } as FrigadeAssistantApi;
    window.FrigadeAssistant = stub;
  }
  if (!window.FrigadeAssistant.on) {
    const on = function (event: string, handler: FrigadeEventHandler) {
      (on.q = on.q ?? []).push({event, handler});
    } as FrigadeOnFn;
    window.FrigadeAssistant.on = on;
  }

  const existing = document.head.querySelector<HTMLScriptElement>(`script[src='${FRIGADE_SCRIPT_SRC}']`);
  if (existing && existing.dataset.frigadeTimedOut !== `true`) {
    return existing;
  }
  if (existing) {
    // The element outlived a 10s timeout without loading. Reaching here means
    // a caller is asking again after that grace period, so trade the stalled
    // element for a fresh download rather than attaching listeners to one that
    // may never fire again. An element that loaded late clears the marker in
    // its `load` handler and so is reused above.
    existing.remove();
  }
  const script = document.createElement(`script`);
  script.src = FRIGADE_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
  return script;
}

// The single owner of the script element: one promise per download attempt,
// no matter how many identities are waiting on it. Everything element-related
// - injection, the `load`/`error` listeners, the deadline - lives here, so
// replacing a stalled element can never strand a caller listening to the node
// that was removed.
let _frigadeScriptReady: Promise<boolean> | null = null;

function loadFrigadeScript(): Promise<boolean> {
  if (_frigadeScriptReady) {
    return _frigadeScriptReady;
  }
  const promise = new Promise<boolean>((resolve) => {
    const script = installFrigadeBootstrap();
    let settled = false;
    let timeoutId: number | undefined;
    const settle = (ok: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      // Cancel the timer so it can't mutate the element after the fact -
      // stamping the timed-out marker on a script that has since loaded, or on
      // one the `error` path already removed.
      window.clearTimeout(timeoutId);
      if (!ok) {
        // Failures are retryable: the next call re-attempts the bootstrap.
        _frigadeScriptReady = null;
      }
      resolve(ok);
    };
    if (script.dataset.frigadeReady === `true`) {
      settle(true);
      return;
    }
    script.addEventListener(`load`, () => {
      script.dataset.frigadeReady = `true`;
      // A download that lands after the timeout is still good: clear the
      // marker so the next call reuses this element instead of re-injecting.
      delete script.dataset.frigadeTimedOut;
      settle(true);
    });
    script.addEventListener(`error`, () => {
      // Yank the dead element before settling. It will never fire another
      // event, so leaving it in place would make the next call hit the `src`
      // dedupe in `installFrigadeBootstrap()`, attach listeners to a corpse and
      // wait out a fresh timeout instead of re-downloading.
      script.remove();
      settle(false);
    });
    // The timeout deliberately leaves the element in place: unlike `error`, it
    // says nothing about whether the download failed, and removing a
    // slow-but-live script cancels it. Marking it instead bounds the damage -
    // the element's `load` listener still resolves a later caller if the
    // download lands, and if a caller asks again first,
    // `installFrigadeBootstrap()` trades the stalled element for a fresh one.
    timeoutId = window.setTimeout(() => {
      script.dataset.frigadeTimedOut = `true`;
      settle(false);
    }, 10_000);
  });
  _frigadeScriptReady = promise;
  return promise;
}

// Keyed by publicKey/assistantId/userId so a credential or identity change
// between activations doesn't return the stale promise. Every entry chains off
// the one script promise; the key only tracks which configs have been sent.
const _frigadeReadyPromises = new Map<string, Promise<boolean>>();

// The vendor attaches `show`/`hide`/`close`/`getMode`/`ask` from a React effect
// that runs after its bundle's `load` event, so resolving on `load` alone hands
// callers an API whose methods are still undefined - and every call site uses
// optional chaining, so they would silently no-op.
const FRIGADE_API_READY_TIMEOUT_MS = 10_000;
const FRIGADE_API_POLL_MS = 50;

function whenImperativeApiReady(): Promise<boolean> {
  if (window.FrigadeAssistant?.show) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const deadline = Date.now() + FRIGADE_API_READY_TIMEOUT_MS;
    const timer = window.setInterval(() => {
      if (window.FrigadeAssistant?.show) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() >= deadline) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, FRIGADE_API_POLL_MS);
  });
}

export function loadFrigadeAssistant(options: LoadFrigadeAssistantOptions): Promise<boolean> {
  const {publicKey, assistantId, userId, userProperties, preferences, tools} = options;
  // Empty credentials mean the backend couldn't resolve this environment's
  // Frigade key or assistant yet - skip the bootstrap so the page degrades to
  // "no assistant" instead of loading an unverified widget.
  if (isTestEnv() || !publicKey || !assistantId || !userId) {
    return Promise.resolve(false);
  }
  const cacheKey = `${publicKey}:${assistantId}:${userId}`;
  const cached = _frigadeReadyPromises.get(cacheKey);
  if (cached) {
    return cached;
  }
  // Start the script first: it installs the `window.FrigadeAssistant` stub, so
  // the config below queues on `.q` in call order.
  const scriptReady = loadFrigadeScript();
  // `defaultVisible: false` because the Help menu drives visibility - the
  // assistant must never paint its own launcher button.
  window.FrigadeAssistant?.({
    apiKey: publicKey,
    apiUrl: FRIGADE_API_URL,
    assistantId,
    userId,
    userProperties,
    preferences,
    tools,
    defaultVisible: false,
  });
  const promise = scriptReady.then(async (ok) => {
    // Failures are retryable: the next call for this identity re-sends the
    // config against a fresh script attempt.
    if (!ok) {
      _frigadeReadyPromises.delete(cacheKey);
      return false;
    }
    const apiReady = await whenImperativeApiReady();
    if (!apiReady) {
      _frigadeReadyPromises.delete(cacheKey);
    }
    return apiReady;
  });
  _frigadeReadyPromises.set(cacheKey, promise);
  return promise;
}
